import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import { Skeleton } from "@/components/ui/skeleton";
import { LayoutDashboard, Users, Calendar, Star, Heart, TrendingUp, Info, Hash } from "lucide-react";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { ChartCard, UserTrendChart, BookingTrendChart, EngagementChart, PieChartCard, ServicePopularityChart } from "@/components/admin/dashboard/SafeDashboardCharts";

// Build number for PWA update detection
const BUILD_NUMBER = `${new Date().getFullYear()}.${String(new Date().getMonth() + 1).padStart(2, '0')}.${String(new Date().getDate()).padStart(2, '0')}.${Math.floor(Date.now() / 1000) % 86400}`;

const BuildInfo = () => (
  <div className="flex items-center justify-end mb-4 text-xs text-muted-foreground">
    <Hash className="h-3 w-3 mr-1" />
    <span>Build: {BUILD_NUMBER}</span>
  </div>
);

interface MetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  loading?: boolean;
  trend?: number;
  color?: string;
}

const MetricCard = ({ title, value, description, icon, loading = false, trend, color = "bg-primary" }: MetricCardProps) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={`p-2 rounded-full ${color}/10`}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-28" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
      {trend !== undefined && (
        <CardFooter className="p-2">
          <div className={`flex items-center text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp className={`h-3 w-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
            <span>{Math.abs(trend)}% {trend >= 0 ? 'increase' : 'decrease'}</span>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

const AdminDashboard = () => {
  const { data: isAdmin, isLoading: isAdminLoading } = useAdminCheckQuery();
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    newUsers: 0,
    totalBookings: 0,
    pendingBookings: 0,
    totalReviews: 0,
    avgRating: 0,
    totalFavorites: 0
  });
  const [userTrend, setUserTrend] = useState<any[]>([]);
  const [bookingTrend, setBookingTrend] = useState<any[]>([]);
  const [reviewTrend, setReviewTrend] = useState<any[]>([]);
  const [roleDistribution, setRoleDistribution] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState("week");

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Get total users count
        const { count: totalUsers } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        // Get new users in last 7 days
        const lastWeek = new Date();
        lastWeek.setDate(lastWeek.getDate() - 7);
        const { count: newUsers } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .gt('updated_at', lastWeek.toISOString());

        // Get bookings counts
        const { count: totalBookings } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true });

        const { count: pendingBookings } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'new');

        // Get reviews data
        const { count: totalReviews } = await supabase
          .from('reviews')
          .select('*', { count: 'exact', head: true });

        const { data: reviewsData } = await supabase
          .from('reviews')
          .select('rating');

        const avgRating = reviewsData 
          ? (reviewsData.reduce((acc, review) => acc + review.rating, 0) / (reviewsData.length || 1)).toFixed(1)
          : 0;

        // Get favorites count
        const { count: totalFavorites } = await supabase
          .from('favorites')
          .select('*', { count: 'exact', head: true });

        setMetrics({
          totalUsers: totalUsers || 0,
          newUsers: newUsers || 0,
          totalBookings: totalBookings || 0,
          pendingBookings: pendingBookings || 0,
          totalReviews: totalReviews || 0,
          avgRating: parseFloat(avgRating as string),
          totalFavorites: totalFavorites || 0
        });

        // Generate user trend data (mocked for now, could be replaced with real data)
        generateMockTrendData();

        // Get role distribution
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('role');

        if (rolesData) {
          const roleCounts: Record<string, number> = {};
          rolesData.forEach(item => {
            roleCounts[item.role] = (roleCounts[item.role] || 0) + 1;
          });

          const roleDistData = Object.entries(roleCounts).map(([name, value]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            value
          }));
          
          // Add regular users (those without specific roles)
          if (totalUsers) {
            const totalWithRoles = rolesData.length;
            const regularUsers = totalUsers - totalWithRoles;
            if (regularUsers > 0) {
              roleDistData.push({ name: "Regular", value: regularUsers });
            }
          }
          
          setRoleDistribution(roleDistData);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const generateMockTrendData = () => {
    const periods = {
      week: 7,
      month: 30,
      year: 12 // For year, we'll use months instead of days
    };
    
    const days = periods[selectedPeriod as keyof typeof periods];
    const userTrendData = [];
    const bookingTrendData = [];
    const reviewTrendData = [];
    
    // User signups trend (growing pattern)
    let userBase = Math.floor(Math.random() * 5) + 1;
    for (let i = 0; i < days; i++) {
      const date = new Date();
      let label;
      
      if (selectedPeriod === 'year') {
        date.setMonth(date.getMonth() - i);
        label = date.toLocaleString('default', { month: 'short' });
      } else {
        date.setDate(date.getDate() - i);
        label = date.toLocaleString('default', { weekday: 'short' });
        if (selectedPeriod === 'month' && i % 5 === 0) {
          label = date.getDate().toString();
        }
      }
      
      const growth = Math.floor(Math.random() * 5) + userBase;
      userBase = userBase + Math.floor(Math.random() * 2);
      
      userTrendData.unshift({
        name: label,
        Users: growth
      });
      
      // Bookings follow users with some randomness
      bookingTrendData.unshift({
        name: label,
        Bookings: Math.max(0, Math.floor(growth * 0.7 + Math.random() * 5 - 2))
      });
      
      // Reviews are fewer than bookings
      reviewTrendData.unshift({
        name: label,
        Reviews: Math.max(0, Math.floor(growth * 0.3 + Math.random() * 3 - 1)),
        Favorites: Math.max(0, Math.floor(growth * 0.5 + Math.random() * 4 - 1))
      });
    }
    
    setUserTrend(userTrendData);
    setBookingTrend(bookingTrendData);
    setReviewTrend(reviewTrendData);
  };

  useEffect(() => {
    generateMockTrendData();
  }, [selectedPeriod]);

  if (isAdminLoading) {
    return (
      <AdminLayout title="Dashboard Overview">
        <div className="flex items-center justify-center h-64">
          <Skeleton className="h-32 w-32 rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard Overview">
      <ErrorBoundary componentName="AdminDashboard">
        <div className="flex flex-col gap-6">
          <BuildInfo />
          <Tabs defaultValue="metrics" className="w-full">
            <div className="flex justify-between items-center mb-4">
              <TabsList>
                <TabsTrigger value="metrics">Metrics</TabsTrigger>
                <TabsTrigger value="trends">Trends</TabsTrigger>
                <TabsTrigger value="distribution">Distribution</TabsTrigger>
              </TabsList>
              
              <div className="flex items-center space-x-2">
                <select 
                  className="rounded border p-1 text-sm"
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                >
                  <option value="week">Last 7 days</option>
                  <option value="month">Last 30 days</option>
                  <option value="year">Last year</option>
                </select>
                <div className="text-xs text-muted-foreground flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  <span>Demo data</span>
                </div>
              </div>
            </div>
            
            <ErrorBoundary componentName="Metrics Tab">
              <TabsContent value="metrics" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <MetricCard 
                    title="Total Users" 
                    value={metrics.totalUsers} 
                    description={`${metrics.newUsers} new in last 7 days`}
                    icon={<Users className="h-4 w-4 text-primary" />} 
                    loading={isLoading} 
                    trend={12}
                  />
                  
                  <MetricCard 
                    title="Total Bookings" 
                    value={metrics.totalBookings} 
                    description={`${metrics.pendingBookings} pending approvals`}
                    icon={<Calendar className="h-4 w-4 text-indigo-500" />} 
                    loading={isLoading} 
                    trend={8}
                    color="bg-indigo-500"
                  />
                  
                  <MetricCard 
                    title="Reviews" 
                    value={metrics.totalReviews} 
                    description={`${metrics.avgRating} average rating`}
                    icon={<Star className="h-4 w-4 text-yellow-500" />} 
                    loading={isLoading} 
                    trend={5}
                    color="bg-yellow-500"
                  />
                  
                  <MetricCard 
                    title="Favorites" 
                    value={metrics.totalFavorites} 
                    description="Total items favorited"
                    icon={<Heart className="h-4 w-4 text-red-500" />} 
                    loading={isLoading} 
                    trend={-3}
                    color="bg-red-500"
                  />
                </div>
                
                <div className="grid gap-4 md:grid-cols-2 mt-4">
                  <ChartCard title="User Growth" description="New user signups over time">
                    <UserTrendChart data={userTrend} isLoading={isLoading} />
                  </ChartCard>
                  
                  <ChartCard title="Booking Activity" description="Booking trends over time">
                    <BookingTrendChart data={bookingTrend} isLoading={isLoading} />
                  </ChartCard>
                </div>
              </TabsContent>
            </ErrorBoundary>
            
            <ErrorBoundary componentName="Trends Tab">
              <TabsContent value="trends" className="space-y-4">
                <ChartCard title="Engagement Metrics" description="Reviews and favorites over time" height="h-96">
                  <EngagementChart data={reviewTrend} isLoading={isLoading} />
                </ChartCard>
                
                <div className="grid gap-4 md:grid-cols-2 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Daily Metrics Snapshot</CardTitle>
                      <CardDescription>Today's performance summary</CardDescription>
                    </CardHeader>
                    <CardContent className="h-80">
                      <div className="flex flex-col h-full justify-center">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col items-center justify-center p-4 bg-blue-50 rounded-lg">
                            <Users className="h-8 w-8 text-blue-500 mb-2" />
                            <span className="text-sm font-medium text-gray-600">New Users</span>
                            <span className="text-2xl font-bold text-blue-600">
                              {isLoading ? <Skeleton className="h-8 w-16" /> : '12'}
                            </span>
                          </div>
                          
                          <div className="flex flex-col items-center justify-center p-4 bg-purple-50 rounded-lg">
                            <Calendar className="h-8 w-8 text-purple-500 mb-2" />
                            <span className="text-sm font-medium text-gray-600">Bookings</span>
                            <span className="text-2xl font-bold text-purple-600">
                              {isLoading ? <Skeleton className="h-8 w-16" /> : '8'}
                            </span>
                          </div>
                          
                          <div className="flex flex-col items-center justify-center p-4 bg-amber-50 rounded-lg">
                            <Star className="h-8 w-8 text-amber-500 mb-2" />
                            <span className="text-sm font-medium text-gray-600">Reviews</span>
                            <span className="text-2xl font-bold text-amber-600">
                              {isLoading ? <Skeleton className="h-8 w-16" /> : '3'}
                            </span>
                          </div>
                          
                          <div className="flex flex-col items-center justify-center p-4 bg-pink-50 rounded-lg">
                            <Heart className="h-8 w-8 text-pink-500 mb-2" />
                            <span className="text-sm font-medium text-gray-600">Favorites</span>
                            <span className="text-2xl font-bold text-pink-600">
                              {isLoading ? <Skeleton className="h-8 w-16" /> : '15'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader>
                      <CardTitle>Key Metrics</CardTitle>
                      <CardDescription>Performance indicators</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">User Retention Rate</p>
                            <div className="text-2xl font-bold">78%</div>
                          </div>
                          <div className="text-green-500 bg-green-50 px-2.5 py-0.5 rounded-full text-xs font-medium">
                            +2.5%
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Booking Completion Rate</p>
                            <div className="text-2xl font-bold">64%</div>
                          </div>
                          <div className="text-red-500 bg-red-50 px-2.5 py-0.5 rounded-full text-xs font-medium">
                            -1.8%
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Average Session Duration</p>
                            <div className="text-2xl font-bold">4:23</div>
                          </div>
                          <div className="text-green-500 bg-green-50 px-2.5 py-0.5 rounded-full text-xs font-medium">
                            +0:15
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-sm font-medium">Customer Satisfaction</p>
                            <div className="text-2xl font-bold">4.7/5</div>
                          </div>
                          <div className="text-green-500 bg-green-50 px-2.5 py-0.5 rounded-full text-xs font-medium">
                            +0.2
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </ErrorBoundary>
            
            <ErrorBoundary componentName="Distribution Tab">
              <TabsContent value="distribution" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <ChartCard title="User Roles Distribution" description="Breakdown of user types">
                    <PieChartCard data={roleDistribution} isLoading={isLoading} />
                  </ChartCard>
                  
                  <ChartCard title="Bookings by Status" description="Current booking status breakdown">
                    <PieChartCard 
                      data={[
                        { name: "Completed", value: 45 },
                        { name: "Pending", value: 30 },
                        { name: "Cancelled", value: 15 },
                        { name: "No-show", value: 10 }
                      ]} 
                      isLoading={isLoading} 
                      colors={['#4ade80', '#facc15', '#f87171', '#94a3b8']} 
                    />
                  </ChartCard>
                </div>
                
                <ChartCard title="Service Popularity" description="Most booked services">
                  <ServicePopularityChart
                    data={[
                      { name: "Hair Styling", value: 120 },
                      { name: "Massage Therapy", value: 98 },
                      { name: "Nail Services", value: 86 },
                      { name: "Facial Treatment", value: 71 },
                      { name: "Home Cleaning", value: 65 },
                      { name: "Personal Training", value: 52 }
                    ]}
                    isLoading={isLoading}
                  />
                </ChartCard>
              </TabsContent>
            </ErrorBoundary>
          </Tabs>
        </div>
      </ErrorBoundary>
    </AdminLayout>
  );
};

export default AdminDashboard;
